// Shared device/browser detection used by the install-guide popover
// (components/InstallGuide.js) and the full download-guide page
// (pages/DownloadPage.js), so the two never drift out of sync.

export const ua = () => navigator.userAgent || '';

// ─── Version parsing (best-effort, used only where it changes real behavior) ──
export const getIOSVersion = () => {
    const match = ua().match(/OS (\d+)_(\d+)/);
    return match ? parseInt(match[1], 10) : null;
};

// ─── Browser detection ─────────────────────────────────────────────────────
export const isChromeIOS      = () => /CriOS/i.test(ua());
export const isFirefoxIOS     = () => /FxiOS/i.test(ua());
export const isSamsungBrowser = () => /SamsungBrowser/i.test(ua());
export const isFirefoxAndroid = () => /Android/i.test(ua()) && /Firefox/i.test(ua()) && !/SamsungBrowser/i.test(ua());

// In-app browsers (Instagram/Facebook/TikTok/etc. + generic Android WebView)
// frequently can't install PWAs at all, and their UI varies too wildly to
// point at reliably — better to tell people to switch browsers.
export const isInAppBrowser = () =>
    /FBAN|FBAV|Instagram|Line\/|MicroMessenger|TikTok|Snapchat/i.test(ua()) ||
    /; ?wv\)/i.test(ua());

// ─── Device detection ───────────────────────────────────────────────────────
// iPadOS 13+ reports as MacIntel — use maxTouchPoints to catch it
export const isIPad = () =>
    /iPad/i.test(ua()) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);

export const isIPhone = () =>
    /iPhone|iPod/i.test(ua()) && !window.MSStream;

export const isAndroid = () => /Android/i.test(ua());

export const isMobileDevice = () =>
    isIPhone() || isIPad() || isAndroid();

export const isInStandaloneMode = () =>
    window.matchMedia('(display-mode: standalone)').matches ||
    window.matchMedia('(display-mode: fullscreen)').matches ||
    window.navigator.standalone === true;

// ─── Locale / orientation ───────────────────────────────────────────────────
// Native browser chrome follows the DEVICE's system language, not this page's
// text — the browser's own reported language is the best signal we have from
// JS for whether that chrome is likely mirrored.
const RTL_LANGS = ['ar', 'he', 'fa', 'ur', 'ps', 'sd', 'ug', 'yi', 'dv', 'ku'];
export const isRTL = () => {
    const lang = (navigator.language || navigator.userLanguage || '').toLowerCase();
    return RTL_LANGS.some((code) => lang === code || lang.startsWith(`${code}-`));
};

export const isLandscape = () => window.matchMedia('(orientation: landscape)').matches;

// "end" = right in LTR, left in RTL — mirrors how RTL system languages
// actually flip native browser chrome.
export const endSide = () => (isRTL() ? 'left' : 'right');
